/**
 * OPOS – Offene Posten
 *
 * Zwei Spalten: Debitoren (Kundenrechnungen) | Kreditoren (Lieferantenrechnungen)
 * Oben: Zusammenfassungskacheln (Gesamtforderung, Faelligkeiten)
 * Darunter: Liste aller Geschaeftspartner mit ausklappbaren Rechnungsdetails
 */

import { useState, useEffect, useMemo } from 'react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import { useAnonymize, type Anonymizer } from '@/hooks/useAnonymize.ts'
import type { ColumnDef } from '@/components/ui'
import {
  Loader2, AlertCircle, RefreshCw, ChevronRight, ChevronDown,
  TrendingUp, TrendingDown, Clock, AlertTriangle, Calendar,
  Lock, LockOpen, CreditCard,
} from 'lucide-react'
import { Badge, DataTable } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OposInvoice {
  DocEntry: number
  DocNum: number
  CardCode: string
  CardName: string
  DocTotal: number
  PaidToDate?: number
  DocCurrency: string
  DocDate: string
  DocDueDate?: string
  DocType: 'Invoice' | 'PurchaseInvoice' | 'CreditNote' | 'PurchaseCreditNote' | 'DownPayment' | 'PurchaseDownPayment' | 'IncomingPaymentOnAccount' | 'VendorPaymentOnAccount'
  /** Zahlungssperre: 'tYES' = Rechnung gesperrt */
  PaymentBlock?: 'tYES' | 'tNO'
  /** Zahlweg-Code in SAP (z.B. "KK" oder "[L]_KK" fuer Kreditkarte) */
  PaymentMethod?: string
  tenantId: string
  tenantName: string
  tenantCode: string
}

interface BPPayment {
  DocEntry: number
  DocNum: number
  DocDate: string
  DocTotal: number
  DocCurrency: string
  Remarks?: string
}

interface OposEndpointTiming {
  path: string
  ok: boolean
  durationMs: number
  records: number
  error?: string
}

interface OposDebugEntry {
  tenantId: string
  tenantName: string
  counts: Record<string, number>
  timings?: OposEndpointTiming[]
  /** true wenn mindestens ein SAP-Endpoint fehlgeschlagen ist. */
  partial?: boolean
  /** Unix ms: wann diese Daten aus SAP geladen wurden. */
  loadedAt?: number
  /** true wenn die Daten aus dem DB-Snapshot stammen (SAP nicht erreichbar oder cachedOnly). */
  stale?: boolean
}

interface BPBalance {
  CardCode: string
  /** SAP BusinessPartners.Balance: positiv = Partner schuldet uns Geld.
   *  Fuer Debitoren: positiv = Forderung. Fuer Kreditoren: positiv = Verbindlichkeit (SAP-Konvention). */
  Balance: number
  CardType: 'cCustomer' | 'cSupplier'
  tenantId: string
}

interface OposResponse {
  items: OposInvoice[]
  errors: Array<{ tenantId: string; tenantName: string; error: string }>
  debug?: OposDebugEntry[]
  dunningBlocked?: string[]
  bpBalances?: BPBalance[]
  /** Fehlermeldungen aus der BP-Saldo-Abfrage (fuer Debug) */
  bpErrors?: string[]
  /** true wenn mindestens ein Mandant aus DB-Snapshot geliefert wurde (SAP war nicht erreichbar). */
  stale?: boolean
  /** true wenn mindestens ein SAP-Endpoint fehlgeschlagen ist → Ergebnis unvollstaendig. */
  partial?: boolean
  cachedAt?: number | null
}

interface BusinessPartnerGroup {
  cardCode: string
  cardName: string
  tenantId: string
  currency: string
  /** Authoritativer SAP-Kontensaldo (aus BusinessPartners.Balance). Wenn vorhanden, hat dieser Vorrang. */
  sapBalance?: number
  /** Berechneter Saldo aus Einzelbelegen (kann von sapBalance abweichen wenn Buchungen via Konto laufen) */
  computedBalance: number
  totalOpen: number
  invoices: OposInvoice[]
  /** Anzahl Rechnungen mit aktiver Zahlungssperre */
  paymentBlockedCount: number
}

// ─── Debug types ─────────────────────────────────────────────────────────────

interface DebugPartnerGroup {
  cardCode: string
  cardName: string
  tenantId: string
  computedBalance: number
  sapBalance: number | undefined
  totalOpen: number
  shown: boolean
  filterReason: string
  invoiceCount: number
  docTypes: string[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Gutschriften haben negativen Effekt auf den Saldo (reduzieren Forderung/Verbindlichkeit) */
function isCreditNote(inv: OposInvoice): boolean {
  return inv.DocType === 'CreditNote' || inv.DocType === 'PurchaseCreditNote'
}

/** Zahlung auf Konto (noch nicht gegen Rechnung abgestimmt) */
function isPaymentOnAccount(inv: OposInvoice): boolean {
  return inv.DocType === 'IncomingPaymentOnAccount' || inv.DocType === 'VendorPaymentOnAccount'
}

const DOC_TYPE_SHORT: Record<string, string> = {
  Invoice: 'Rechnung',
  PurchaseInvoice: 'Eingangsrechnung',
  CreditNote: 'Gutschrift',
  PurchaseCreditNote: 'Eingangsgutschrift',
  DownPayment: 'Anzahlung',
  PurchaseDownPayment: 'Eingangs-Anzahlung',
  IncomingPaymentOnAccount: 'Zahlung a.K.',
  VendorPaymentOnAccount: 'Zahlung a.K.',
}

/** Kreditkarte: Zahlweg enthaelt "KK" – wird automatisch belastet, kein manueller Handlungsbedarf */
function isKreditkarte(inv: OposInvoice): boolean {
  return typeof inv.PaymentMethod === 'string' && inv.PaymentMethod.toUpperCase().includes('KK')
}

function openBalance(inv: OposInvoice): number {
  if (isPaymentOnAccount(inv)) return -inv.DocTotal // reduziert offene Forderung/Verbindlichkeit
  const base = inv.DocTotal - (inv.PaidToDate ?? 0)
  return isCreditNote(inv) ? -base : base
}

function parseDue(inv: OposInvoice): Date | null {
  const raw = inv.DocDueDate ?? inv.DocDate
  if (!raw) return null
  return new Date(raw.slice(0, 10))
}

function daysDiff(date: Date): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)
  return Math.round((date.getTime() - today.getTime()) / 86_400_000)
}

function fmt(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount)
}

function fmtDate(iso?: string): string {
  if (!iso) return '–'
  return new Date(iso.slice(0, 10)).toLocaleDateString('de-DE')
}

/** Column-Definitionen fuer die Debug-Partnergruppen-Tabellen (Deb + Kred). */
function debugPartnerColumns(a: Anonymizer): ColumnDef<DebugPartnerGroup, unknown>[] {
  return [
    { accessorKey: 'cardCode', header: 'CardCode', cell: ({ row }) => <span className="font-mono">{a.code(row.original.cardCode)}</span> },
    { accessorKey: 'cardName', header: 'Name', cell: ({ row }) => <span className="truncate max-w-[140px] block">{a.name(row.original.cardName)}</span> },
    { accessorKey: 'computedBalance', header: 'computedBal', cell: ({ row }) => (
      <span className={cn('block text-right tabular-nums', row.original.computedBalance > 0.01 ? 'text-green-700' : 'text-gray-400')}>
        {a.fmtCurrency(row.original.computedBalance)}
      </span>
    ) },
    { accessorKey: 'sapBalance', header: 'sapBalance', cell: ({ row }) => (
      <span className={cn('block text-right tabular-nums', row.original.sapBalance === undefined ? 'text-red-500' : row.original.sapBalance > 0.01 ? 'text-green-700' : 'text-blue-600')}>
        {row.original.sapBalance !== undefined ? a.fmtCurrency(row.original.sapBalance) : '—'}
      </span>
    ) },
    { accessorKey: 'totalOpen', header: 'totalOpen', cell: ({ row }) => (
      <span className="block text-right font-semibold tabular-nums">{a.fmtCurrency(row.original.totalOpen)}</span>
    ) },
    { accessorKey: 'invoiceCount', header: '#Belege', cell: ({ row }) => (
      <span className="block text-right text-gray-500 tabular-nums">{row.original.invoiceCount}</span>
    ) },
    { accessorKey: 'filterReason', header: 'Status', cell: ({ row }) => (
      <span className={cn('text-xs', row.original.shown ? 'text-green-700' : 'text-red-600')}>{row.original.filterReason}</span>
    ) },
  ]
}

/** Debug: alle Partnergruppen VOR Filter, mit Grund warum gezeigt/gefiltert */
function buildDebugGroups(
  invoices: OposInvoice[],
  bpBalanceMap: Map<string, number>,
): DebugPartnerGroup[] {
  const map = new Map<string, { cardCode: string; cardName: string; tenantId: string; computedBalance: number; invoices: OposInvoice[] }>()
  for (const inv of invoices) {
    const key = `${inv.tenantId}:${inv.CardCode}`
    if (!map.has(key)) map.set(key, { cardCode: inv.CardCode, cardName: inv.CardName, tenantId: inv.tenantId, computedBalance: 0, invoices: [] })
    const g = map.get(key)!
    g.computedBalance += openBalance(inv)
    g.invoices.push(inv)
  }
  return Array.from(map.values()).map((g) => {
    const bpKey = `${g.tenantId}:${g.cardCode}`
    const sapBalance = bpBalanceMap.get(bpKey)
    const shown = g.computedBalance > 0.01 || (sapBalance !== undefined && sapBalance > 0.01)
    let filterReason: string
    if (shown) {
      if (sapBalance !== undefined && sapBalance > 0.01) filterReason = `✓ SAP-Saldo: ${fmt(sapBalance)}`
      else filterReason = `✓ computed: ${fmt(g.computedBalance)}`
    } else {
      const parts: string[] = []
      parts.push(`computed=${fmt(g.computedBalance)}`)
      parts.push(sapBalance !== undefined ? `SAP=${fmt(sapBalance)}` : 'kein SAP-Saldo')
      filterReason = `✗ Gefiltert (${parts.join(', ')})`
    }
    const docTypes = [...new Set(g.invoices.map((i) => DOC_TYPE_SHORT[i.DocType] ?? i.DocType))]
    const totalOpen = g.computedBalance > 0.01 ? g.computedBalance
      : (sapBalance !== undefined && sapBalance > 0.01) ? sapBalance : g.computedBalance
    return { cardCode: g.cardCode, cardName: g.cardName, tenantId: g.tenantId, computedBalance: g.computedBalance, sapBalance, totalOpen, shown, filterReason, invoiceCount: g.invoices.length, docTypes }
  }).sort((a, b) => b.computedBalance - a.computedBalance)
}

function groupByPartner(
  invoices: OposInvoice[],
  paymentBlockOverrides: Map<string, boolean>,
  bpBalanceMap: Map<string, number>,
): BusinessPartnerGroup[] {
  const map = new Map<string, BusinessPartnerGroup>()
  for (const inv of invoices) {
    const key = `${inv.tenantId}:${inv.CardCode}`
    if (!map.has(key)) {
      map.set(key, {
        cardCode: inv.CardCode, cardName: inv.CardName, tenantId: inv.tenantId,
        currency: inv.DocCurrency, totalOpen: 0, computedBalance: 0, invoices: [], paymentBlockedCount: 0,
      })
    }
    const g = map.get(key)!
    g.computedBalance += openBalance(inv)
    g.invoices.push(inv)
    const overrideKey = `${inv.tenantId}:${inv.DocEntry}:${inv.DocType}`
    const isBlocked = paymentBlockOverrides.has(overrideKey)
      ? paymentBlockOverrides.get(overrideKey)!
      : inv.PaymentBlock === 'tYES'
    if (isBlocked) g.paymentBlockedCount++
  }
  // Set SAP account balance (for debug display and divergence indicator)
  for (const g of map.values()) {
    const bpKey = `${g.tenantId}:${g.cardCode}`
    g.sapBalance = bpBalanceMap.get(bpKey)
  }
  // Filter: show partner when they have open items OR SAP shows a balance > 0.
  // Do NOT filter by sapBalance alone — partners with negative SAP balance (credit) must stay visible.
  return Array.from(map.values())
    .filter((g) => g.computedBalance > 0.01 || (g.sapBalance !== undefined && g.sapBalance > 0.01))
    .map((g) => ({
      ...g,
      // computedBalance is derived from actual OPOS items (invoices, credit notes, payments on account)
      // and is the most accurate open amount when items were loaded correctly.
      // sapBalance (SAP BusinessPartners.Balance) sometimes omits on-account payments from the net,
      // causing it to show a higher gross amount — so it is only used as a fallback when
      // computedBalance is zero (edge case: items missing from OPOS feed but SAP reports a balance).
      totalOpen: g.computedBalance > 0.01
        ? g.computedBalance
        : (g.sapBalance !== undefined && g.sapBalance > 0.01) ? g.sapBalance : g.computedBalance,
    }))
    .sort((a, b) => b.totalOpen - a.totalOpen)
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

interface SummaryStats {
  total: number
  dueToday: number
  overdue7: number
  overdue14: number
  overdue30: number
  dueSoon7: number
  dueSoon14: number
  dueSoon30: number
  /** Summe aller offenen Kreditkarten-Rechnungen (Zahlweg=KK) */
  kreditkarte: number
  currency: string
}

function calcStats(invoices: OposInvoice[], groups?: BusinessPartnerGroup[]): SummaryStats {
  // Gesamtsaldo = Summe der SAP-Kontensalden (authoritative), nicht berechnet aus Einzelbelegen
  const total = groups ? groups.reduce((acc, g) => acc + g.totalOpen, 0) : 0

  let dueToday = 0, overdue7 = 0, overdue14 = 0, overdue30 = 0
  let dueSoon7 = 0, dueSoon14 = 0, dueSoon30 = 0
  let kreditkarte = 0

  for (const inv of invoices) {
    const bal = openBalance(inv)
    if (bal <= 0.01) continue // kein Beitrag zu Faelligkeitskacheln
    if (isPaymentOnAccount(inv)) continue // keine Faelligkeit bei Zahlungen auf Konto
    // KK-Rechnungen: separat zaehlen, NICHT in Ueberfaellig-Baender einfliessen lassen
    if (isKreditkarte(inv)) {
      kreditkarte += bal
      continue
    }
    const dueDate = parseDue(inv)
    if (!dueDate) continue
    const diff = daysDiff(dueDate)
    if (diff === 0) dueToday += bal
    // Ueberfaellig: exklusive Baender (7-13 Tage, 14-29 Tage, 30+ Tage)
    if (diff <= -7 && diff > -14) overdue7 += bal
    if (diff <= -14 && diff > -30) overdue14 += bal
    if (diff <= -30) overdue30 += bal
    // Bald faellig: exklusive Baender (1-7 Tage, 8-14 Tage, 15-30 Tage)
    if (diff > 0 && diff <= 7) dueSoon7 += bal
    if (diff > 7 && diff <= 14) dueSoon14 += bal
    if (diff > 14 && diff <= 30) dueSoon30 += bal
  }

  const currency = invoices[0]?.DocCurrency ?? 'EUR'
  return { total, dueToday, overdue7, overdue14, overdue30, dueSoon7, dueSoon14, dueSoon30, kreditkarte, currency }
}

interface SummaryCardProps {
  label: string
  amount: number
  currency: string
  variant?: 'primary' | 'danger' | 'warning' | 'info' | 'kk'
  icon?: React.ReactNode
  small?: boolean
}

function SummaryCard({ label, amount, currency, variant = 'info', icon, small }: SummaryCardProps) {
  const a = useAnonymize()
  const bg = {
    primary: 'bg-blue-50 border-blue-200',
    danger: 'bg-red-50 border-red-200',
    warning: 'bg-amber-50 border-amber-200',
    info: 'bg-gray-50 border-gray-200',
    kk: 'bg-amber-50 border-amber-200',
  }[variant]
  const textColor = {
    primary: 'text-blue-700',
    danger: 'text-red-700',
    warning: 'text-amber-700',
    info: 'text-gray-700',
    kk: 'text-amber-700',
  }[variant]

  return (
    <div className={cn('border rounded-lg p-3 flex flex-col gap-1', bg, small ? 'text-xs' : '')}>
      <div className={cn('flex items-center gap-1.5 text-gray-500', small ? 'text-xs' : 'text-sm')}>
        {icon}
        <span>{label}</span>
      </div>
      <div className={cn('font-semibold', textColor, small ? 'text-sm' : 'text-lg')}>
        {a.fmtCurrency(amount, currency)}
      </div>
    </div>
  )
}

// ─── Unified Invoice/Payment Rows (SOLL/HABEN) ───────────────────────────────

/** Wrapper um den Sperr-Toggle, damit der eigene Loading-State pro Zeile bleibt. */
function PaymentBlockToggle({ inv, isPaymentBlocked, onTogglePaymentBlock }: {
  inv: OposInvoice
  isPaymentBlocked: boolean
  onTogglePaymentBlock?: (inv: OposInvoice, block: boolean) => Promise<void>
}) {
  const [toggling, setToggling] = useState(false)
  async function handleToggle() {
    if (!onTogglePaymentBlock || toggling) return
    setToggling(true)
    try { await onTogglePaymentBlock(inv, !isPaymentBlocked) } finally { setToggling(false) }
  }
  return (
    <button
      onClick={handleToggle}
      disabled={toggling}
      title={isPaymentBlocked ? 'Zahlungssperre aufheben' : 'Zahlungssperre setzen'}
      className={cn(
        'flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors disabled:opacity-50',
        isPaymentBlocked
          ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
          : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600',
      )}
    >
      {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : isPaymentBlocked ? <LockOpen className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      {isPaymentBlocked ? 'Entsperren' : 'Sperren'}
    </button>
  )
}

interface UnifiedInvoiceMeta {
  isCredit: boolean
  isKK: boolean
  isOnAccount: boolean
  isHaben: boolean
  diff: number | null
  isOverdue: boolean
  isDueToday: boolean
  isPaymentBlocked: boolean
  canToggle: boolean
}

function computeUnifiedInvoiceMeta(inv: OposInvoice, paymentBlockOverrides: Map<string, boolean>): UnifiedInvoiceMeta {
  const isCredit = isCreditNote(inv)
  const isKK = isKreditkarte(inv)
  const isOnAccount = isPaymentOnAccount(inv)
  const isHaben = isCredit || isOnAccount
  const dueDate = parseDue(inv)
  const diff = dueDate ? daysDiff(dueDate) : null
  const isOverdue = !isHaben && !isKK && diff !== null && diff < 0
  const isDueToday = !isHaben && !isKK && diff === 0
  const overrideKey = `${inv.tenantId}:${inv.DocEntry}:${inv.DocType}`
  const isPaymentBlocked = paymentBlockOverrides.has(overrideKey)
    ? paymentBlockOverrides.get(overrideKey)!
    : inv.PaymentBlock === 'tYES'
  const canToggle = !isCredit && !isOnAccount && (inv.DocType === 'Invoice' || inv.DocType === 'PurchaseInvoice')
  return { isCredit, isKK, isOnAccount, isHaben, diff, isOverdue, isDueToday, isPaymentBlocked, canToggle }
}

// ─── Business Partner Row ─────────────────────────────────────────────────────

interface PartnerRowProps {
  group: BusinessPartnerGroup
  overdueDays: number
  isDunningBlocked?: boolean
  paymentBlockOverrides: Map<string, boolean>
  onTogglePaymentBlock?: (inv: OposInvoice, block: boolean) => Promise<void>
  /** true = Kreditoren (VendorPayments), false/undefined = Debitoren (IncomingPayments) */
  isKreditor?: boolean
}

function PartnerRow({ group, overdueDays, isDunningBlocked, paymentBlockOverrides, onTogglePaymentBlock, isKreditor }: PartnerRowProps) {
  const a = useAnonymize()
  const [expanded, setExpanded] = useState(false)
  const [payments, setPayments] = useState<BPPayment[] | null>(null)
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)

  // Zahlungen laden, sobald erstmalig aufgeklappt wird
  useEffect(() => {
    if (!expanded || payments !== null) return
    setLoadingPayments(true)
    setPaymentsError(null)

    // fromDate: 30 Tage vor der aeltesten offenen Rechnung dieser Gruppe.
    // Filtert alte abgestimmte Zahlungen fuer laengst geschlossene Rechnungen heraus.
    const invoiceDates = group.invoices.map((i) => i.DocDate ?? '').filter(Boolean).sort()
    const oldestDate = invoiceDates[0]
    let fromDate: string | undefined
    if (oldestDate) {
      const d = new Date(oldestDate.slice(0, 10))
      d.setDate(d.getDate() - 30)
      fromDate = d.toISOString().slice(0, 10)
    }

    const params = new URLSearchParams({
      tenantId: group.tenantId,
      cardCode: group.cardCode,
      docType: isKreditor ? 'vendor' : 'incoming',
    })
    if (fromDate) params.set('fromDate', fromDate)

    apiClient.get<BPPayment[]>(`/buchhaltung/sap/bp-payments?${params.toString()}`)
      .then((res) => setPayments(res.data))
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
          ?? (err as { message?: string })?.message ?? 'Fehler beim Laden'
        setPaymentsError(msg)
        setPayments([])
      })
      .finally(() => setLoadingPayments(false))
  }, [expanded]) // eslint-disable-line react-hooks/exhaustive-deps

  const isOverdue = overdueDays > 0
  const isCritical = overdueDays >= 30
  const hasPaymentBlocked = group.paymentBlockedCount > 0
  const allPaymentBlocked = group.paymentBlockedCount === group.invoices.length && group.invoices.length > 0

  // Pruefen ob alle positiven Rechnungen per KK bezahlt werden
  const nonCreditInvoices = group.invoices.filter((i) => !isCreditNote(i) && !isPaymentOnAccount(i))
  const allKreditkarte = nonCreditInvoices.length > 0 && nonCreditInvoices.every(isKreditkarte)
  const hasKreditkarte = nonCreditInvoices.some(isKreditkarte)

  // Zahlungssperre: gedaempftes Orange/Grau (wenn alle blockiert = stark gedaempft)
  // Mahnsperre: blasseres Rot
  // Ueberfaellig: kraeftiges Rot
  // Kreditkarte: leichtes Amber (automatisch gebucht, kein Handlungsbedarf)
  const borderClass = isDunningBlocked
    ? 'border-rose-200'
    : allPaymentBlocked
      ? 'border-orange-200'
      : hasPaymentBlocked
        ? 'border-orange-200'
        : isCritical
          ? 'border-red-400'
          : isOverdue
            ? 'border-red-300'
            : allKreditkarte
              ? 'border-amber-200'
              : 'border-gray-200'

  const headerBg = isDunningBlocked
    ? 'bg-rose-50/50 hover:bg-rose-50'
    : allPaymentBlocked
      ? 'bg-gray-50/80 hover:bg-gray-100'
      : hasPaymentBlocked
        ? 'bg-orange-50/30 hover:bg-orange-50/50'
        : isCritical
          ? 'bg-red-50 hover:bg-red-100'
          : isOverdue
            ? 'bg-red-50/60 hover:bg-red-50'
            : allKreditkarte
              ? 'bg-amber-50/40 hover:bg-amber-50/60'
              : 'bg-white hover:bg-gray-50'

  const nameClass = allPaymentBlocked
    ? 'text-gray-500 font-medium'
    : (isOverdue && !isDunningBlocked)
      ? 'text-red-800 font-semibold'
      : isDunningBlocked
        ? 'text-rose-700 font-medium'
        : allKreditkarte
          ? 'text-amber-800 font-medium'
          : 'text-gray-800 font-medium'
  const amountClass = allPaymentBlocked
    ? 'text-gray-400 font-semibold'
    : (isOverdue && !isDunningBlocked)
      ? 'text-red-700 font-bold'
      : isDunningBlocked
        ? 'text-rose-600 font-semibold'
        : allKreditkarte
          ? 'text-amber-700 font-semibold'
          : 'text-gray-900 font-semibold'

  // Unified SOLL/HABEN rows – zusammengefuehrte Belege + Zahlungen, nach Datum sortiert
  const paymentOnAccountEntries = new Set(group.invoices.filter(isPaymentOnAccount).map((i) => i.DocEntry))
  const uniquePayments = (payments ?? []).filter((p) => !paymentOnAccountEntries.has(p.DocEntry))
  type UnifiedEntry = { kind: 'invoice'; inv: OposInvoice } | { kind: 'payment'; payment: BPPayment }
  const unifiedRows: UnifiedEntry[] = [
    ...group.invoices.map((inv) => ({ kind: 'invoice' as const, inv })),
    ...uniquePayments.map((p) => ({ kind: 'payment' as const, payment: p })),
  ].sort((a, b) => {
    const dA = a.kind === 'invoice' ? (a.inv.DocDate ?? '') : (a.payment.DocDate ?? '')
    const dB = b.kind === 'invoice' ? (b.inv.DocDate ?? '') : (b.payment.DocDate ?? '')
    return dA.localeCompare(dB)
  })
  // SOLL: Rechnungen + Anzahlungen (positive Beitraege zur Forderung)
  const totalSoll = group.invoices
    .filter((inv) => !isCreditNote(inv) && !isPaymentOnAccount(inv))
    .reduce((s, inv) => s + inv.DocTotal, 0)
  // HABEN: Gutschriften + Zahlungen auf Konto + individuelle Zahlungsbelege
  const totalHaben =
    group.invoices
      .filter((inv) => isCreditNote(inv) || isPaymentOnAccount(inv))
      .reduce((s, inv) => s + inv.DocTotal, 0) +
    uniquePayments.reduce((s, p) => s + p.DocTotal, 0)
  // Net balance: use totalOpen which is already the correctly computed value (computedBalance preferred)
  const netBalance = group.totalOpen

  return (
    <div className={cn('border rounded-lg overflow-hidden', borderClass)}>
      <button
        className={cn('w-full flex items-center justify-between px-4 py-3 transition-colors text-left', headerBg)}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className={cn('w-4 h-4 shrink-0', isOverdue ? 'text-red-400' : 'text-gray-400')} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', isOverdue ? 'text-red-400' : 'text-gray-400')} />}
          <span className={cn('truncate', nameClass)}>{a.name(group.cardName)}</span>
          <span className="text-xs text-gray-400 shrink-0">{a.code(group.cardCode)}</span>
          {isOverdue && !isDunningBlocked && (
            <span className={cn('shrink-0 text-xs px-1.5 py-0.5 rounded font-medium', isCritical ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-600')}>
              {overdueDays}d überfällig
            </span>
          )}
          {isDunningBlocked && (
            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-rose-100 text-rose-600 border border-rose-200">
              ⚠ Mahnsperre
            </span>
          )}
          {hasPaymentBlocked && (
            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-orange-100 text-orange-600 border border-orange-200">
              ⛔ {allPaymentBlocked ? 'Alle gesperrt' : `${group.paymentBlockedCount} gesperrt`}
            </span>
          )}
          {hasKreditkarte && (
            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-0.5">
              <CreditCard className="w-3 h-3" />Kreditkarte
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <span className="text-xs text-gray-400">{group.invoices.length} Rechnung{group.invoices.length !== 1 ? 'en' : ''}</span>
          <div className="text-right">
            <span className={amountClass}>{a.fmtCurrency(group.totalOpen, group.currency)}</span>
            {/* Wenn SAP-Saldo vorhanden und weicht ab: beide Werte zeigen */}
            {group.sapBalance !== undefined && Math.abs(group.sapBalance - group.computedBalance) > 0.5 && (
              <p className="text-xs text-gray-400 leading-tight">
                SAP {a.fmtCurrency(group.sapBalance, group.currency)} / ber. {a.fmtCurrency(group.computedBalance, group.currency)}
              </p>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 overflow-x-auto">
          {/* Einheitliche SOLL/HABEN-Belegliste */}
          <DataTable<UnifiedEntry>
            data={unifiedRows}
            getRowId={(row: UnifiedEntry) =>
              row.kind === 'invoice' ? `inv-${row.inv.DocEntry}-${row.inv.DocType}` : `pay-${row.payment.DocEntry}`
            }
            loading={loadingPayments && unifiedRows.length === 0}
            enableSorting={false}
            className="min-w-[560px] text-xs"
            rowClassName={(row: UnifiedEntry) => {
              if (row.kind === 'payment') return 'border-l-2 border-l-green-400 bg-green-50/30 hover:bg-green-50/60'
              const meta = computeUnifiedInvoiceMeta(row.inv, paymentBlockOverrides)
              const rowBg = meta.isHaben
                ? 'bg-green-50/30 hover:bg-green-50/60'
                : meta.isPaymentBlocked
                  ? 'bg-orange-50/40 hover:bg-orange-50/60'
                  : meta.isKK
                    ? 'bg-amber-50/30 hover:bg-amber-50/50'
                    : meta.isOverdue
                      ? 'bg-red-50/30 hover:bg-red-50/50'
                      : 'hover:bg-gray-50/60'
              const borderLeft = meta.isHaben
                ? 'border-l-2 border-l-green-300'
                : meta.isPaymentBlocked
                  ? 'border-l-2 border-l-orange-300'
                  : meta.isOverdue
                    ? 'border-l-2 border-l-red-400'
                    : 'border-l-2 border-l-red-200'
              return cn(rowBg, borderLeft)
            }}
            columns={[
              {
                id: 'belegnr',
                header: 'Belegnr.',
                cell: ({ row }) => {
                  const entry = row.original
                  if (entry.kind === 'payment') {
                    const payment = entry.payment
                    return (
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className="font-mono text-gray-600">#{a.docNum(payment.DocNum)}</span>
                        <Badge variant="success" size="sm">Zahlung</Badge>
                        {payment.Remarks && <span className="text-gray-400 truncate max-w-[150px]">{a.freeText(payment.Remarks)}</span>}
                      </div>
                    )
                  }
                  const inv = entry.inv
                  const meta = computeUnifiedInvoiceMeta(inv, paymentBlockOverrides)
                  return (
                    <div className="flex items-center gap-1.5 flex-wrap whitespace-nowrap">
                      <span className="font-mono text-gray-600">#{a.docNum(inv.DocNum)}</span>
                      {meta.isCredit && <Badge variant="success" size="sm">Gutschrift</Badge>}
                      {meta.isOnAccount && <span className="bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-medium">↩ Zahlung a.K.</span>}
                      {meta.isKK && <Badge variant="warning" size="sm"><CreditCard className="w-3 h-3" />KK</Badge>}
                      {meta.isPaymentBlocked && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">⛔ Sperre</span>}
                      {meta.canToggle && (
                        <PaymentBlockToggle
                          inv={inv}
                          isPaymentBlocked={meta.isPaymentBlocked}
                          onTogglePaymentBlock={onTogglePaymentBlock}
                        />
                      )}
                    </div>
                  )
                },
              },
              {
                id: 'datum',
                header: 'Datum',
                cell: ({ row }) => {
                  const docDate = row.original.kind === 'payment' ? row.original.payment.DocDate : row.original.inv.DocDate
                  return <span className="text-gray-500 whitespace-nowrap">{fmtDate(docDate)}</span>
                },
              },
              {
                id: 'faelligkeit',
                header: 'Fälligkeit',
                cell: ({ row }) => {
                  if (row.original.kind === 'payment') return <span className="text-gray-300">–</span>
                  const inv = row.original.inv
                  const meta = computeUnifiedInvoiceMeta(inv, paymentBlockOverrides)
                  if (meta.isHaben) return <span className="text-gray-300">–</span>
                  return (
                    <span className={cn('whitespace-nowrap', meta.isOverdue ? 'text-red-600 font-medium' : meta.isDueToday ? 'text-amber-600 font-medium' : 'text-gray-500')}>
                      {fmtDate(inv.DocDueDate)}
                      {meta.isOverdue && meta.diff !== null && <span className="ml-1 text-red-400">({Math.abs(meta.diff)}d)</span>}
                      {meta.isDueToday && <span className="ml-1 text-amber-400">(heute)</span>}
                    </span>
                  )
                },
              },
              {
                id: 'soll',
                header: 'Soll',
                cell: ({ row }) => {
                  if (row.original.kind === 'payment') return <span className="block text-right text-gray-300">–</span>
                  const inv = row.original.inv
                  const meta = computeUnifiedInvoiceMeta(inv, paymentBlockOverrides)
                  if (meta.isHaben) return <span className="block text-right text-gray-300">–</span>
                  return (
                    <span className={cn('block text-right whitespace-nowrap font-semibold tabular-nums', meta.isPaymentBlocked ? 'text-orange-500' : 'text-red-700')}>
                      {a.fmtCurrency(inv.DocTotal, inv.DocCurrency)}
                    </span>
                  )
                },
              },
              {
                id: 'haben',
                header: 'Haben',
                cell: ({ row }) => {
                  if (row.original.kind === 'payment') {
                    const payment = row.original.payment
                    return (
                      <span className="block text-right whitespace-nowrap text-green-700 font-semibold tabular-nums">
                        {a.fmtCurrency(payment.DocTotal, payment.DocCurrency ?? group.currency)}
                      </span>
                    )
                  }
                  const inv = row.original.inv
                  const meta = computeUnifiedInvoiceMeta(inv, paymentBlockOverrides)
                  if (!meta.isHaben) return <span className="block text-right text-gray-300">–</span>
                  return (
                    <span className="block text-right whitespace-nowrap text-green-700 font-semibold tabular-nums">
                      {a.fmtCurrency(inv.DocTotal, inv.DocCurrency)}
                    </span>
                  )
                },
              },
            ]}
            emptyState={loadingPayments ? null : undefined}
          />
          {paymentsError && <p className="pt-1 text-xs text-red-500">{paymentsError}</p>}
          <div className="mt-2 pt-2 border-t border-gray-200 flex flex-col items-end gap-1">
            {payments !== null && (
              <div className="flex items-baseline gap-6 text-xs font-semibold">
                <span className="text-gray-400">Summe</span>
                <span className="text-red-700 tabular-nums">{a.fmtCurrency(totalSoll, group.currency)}</span>
                <span className="text-green-700 tabular-nums">{a.fmtCurrency(totalHaben, group.currency)}</span>
              </div>
            )}
            <div className="flex items-baseline gap-6 text-sm">
              <span className="font-medium text-gray-600">Gesamt offen</span>
              <span className="font-bold text-blue-700 tabular-nums">{a.fmtCurrency(netBalance, group.currency)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Column ───────────────────────────────────────────────────────────────────

interface OposColumnProps {
  title: string
  subtitle: string
  icon: React.ReactNode
  invoices: OposInvoice[]
  loading: boolean
  variant: 'green' | 'red'
  dunningBlockedSet?: Set<string>
  paymentBlockOverrides: Map<string, boolean>
  onTogglePaymentBlock: (inv: OposInvoice, block: boolean) => Promise<void>
  /** Key: tenantId:CardCode → SAP-Kontensaldo */
  bpBalanceMap: Map<string, number>
  isKreditor?: boolean
}

/** Berechnet den maximalen Ueberfaelligkeitsgrad eines Geschaeftspartners (0 = nicht ueberfaellig) */
function maxOverdueDays(group: BusinessPartnerGroup): number {
  let max = 0
  for (const inv of group.invoices) {
    if (isCreditNote(inv) || isPaymentOnAccount(inv) || isKreditkarte(inv)) continue
    const d = parseDue(inv)
    if (!d) continue
    const diff = daysDiff(d)
    if (diff < 0) max = Math.max(max, Math.abs(diff))
  }
  return max
}

function OposColumn({ title, subtitle, icon, invoices, loading, variant, dunningBlockedSet, paymentBlockOverrides, onTogglePaymentBlock, bpBalanceMap, isKreditor }: OposColumnProps) {
  const a = useAnonymize()
  const groups = useMemo(() => groupByPartner(invoices, paymentBlockOverrides, bpBalanceMap), [invoices, paymentBlockOverrides, bpBalanceMap])  
  // Statistiken auf Basis der SAP-Salden (fuer Gesamtbetrag) + Belegdaten (fuer Faelligkeiten)
  const stats = useMemo(() => calcStats(invoices, groups), [invoices, groups])  

  const isGreen = variant === 'green'
  const headerBg = isGreen ? 'bg-green-600' : 'bg-red-600'
  const headerBorder = isGreen ? 'border-green-700' : 'border-red-700'

  return (
    <div className="flex flex-col gap-3">
      {/* Combined header + total */}
      <div className={cn('rounded-xl border p-5 text-white', headerBg, headerBorder)}>
        {loading ? (
          <div className="flex items-center gap-2 opacity-75">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Lade Daten aus SAP…</span>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              {icon}
              <div>
                <h2 className="text-lg font-bold leading-tight">{title}</h2>
                <p className="text-sm opacity-70 mt-0.5">{subtitle}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs opacity-70 mb-0.5">Gesamt offen</p>
              <p className="text-2xl font-bold tabular-nums">{a.fmtCurrency(stats.total, stats.currency)}</p>
              <p className="text-xs opacity-60 mt-0.5">{groups.length} Partner · {invoices.length} Belege</p>
            </div>
          </div>
        )}
      </div>

      {!loading && (
        <>
          {/* Stats grid: 4 ueberfaellig + 3 bald faellig */}
          <div className="grid grid-cols-4 gap-1.5">
            <SummaryCard label="Heute fällig" amount={stats.dueToday} currency={stats.currency} variant="warning" icon={<Clock className="w-3 h-3" />} small />
            <SummaryCard label="7–13 Tage" amount={stats.overdue7} currency={stats.currency} variant="danger" icon={<AlertTriangle className="w-3 h-3" />} small />
            <SummaryCard label="14–29 Tage" amount={stats.overdue14} currency={stats.currency} variant="danger" icon={<AlertTriangle className="w-3 h-3" />} small />
            <SummaryCard label="30+ Tage" amount={stats.overdue30} currency={stats.currency} variant="danger" icon={<AlertTriangle className="w-3 h-3" />} small />
          </div>
          <div className={cn('grid gap-1.5', isGreen ? 'grid-cols-3' : 'grid-cols-4')}>
            <SummaryCard label="Fällig in 1–7 Tagen" amount={stats.dueSoon7} currency={stats.currency} variant="info" icon={<Calendar className="w-3 h-3" />} small />
            <SummaryCard label="Fällig in 8–14 Tagen" amount={stats.dueSoon14} currency={stats.currency} variant="info" icon={<Calendar className="w-3 h-3" />} small />
            <SummaryCard label="Fällig in 15–30 Tagen" amount={stats.dueSoon30} currency={stats.currency} variant="info" icon={<Calendar className="w-3 h-3" />} small />
            {!isGreen && <SummaryCard label="Kreditkarte" amount={stats.kreditkarte} currency={stats.currency} variant="kk" icon={<CreditCard className="w-3 h-3" />} small />}
          </div>

          {/* Partner list */}
          {groups.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
              Keine offenen Posten vorhanden
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {groups.map((g) => (
                <PartnerRow
                  key={`${g.tenantId}:${g.cardCode}`}
                  group={g}
                  overdueDays={maxOverdueDays(g)}
                  isDunningBlocked={dunningBlockedSet?.has(g.cardCode)}
                  paymentBlockOverrides={paymentBlockOverrides}
                  onTogglePaymentBlock={onTogglePaymentBlock}
                  isKreditor={isKreditor}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Filter Persistence ───────────────────────────────────────────────────────

// ─── Main Page ────────────────────────────────────────────────────────────────

export function OposPage() {
  const a = useAnonymize()
  const [data, setData] = useState<OposResponse | null>(null)
  const [loading, setLoading] = useState(true)
  /** Zeigt an dass gerade eine Hintergrund-Aktualisierung laeuft (Snapshot bereits sichtbar). */
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDebug, setShowDebug] = useState(false)
  // Optimistische Overrides fuer Zahlungssperre (lokal bis zum naechsten SAP-Reload)
  const [paymentBlockOverrides, setPaymentBlockOverrides] = useState<Map<string, boolean>>(() => new Map())

  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const fetchTenantsIfNeeded = useTenantStore((s) => s.fetchTenants)
  const tenantLoaded = useTenantStore((s) => s.loaded)

  // Globale Mandantenauswahl aus dem Store (via TenantContextBar gesteuert)
  const selectedTenants = useMemo(() => new Set(activeTenantIds), [activeTenantIds])

  useEffect(() => {
    if (!tenantLoaded) void fetchTenantsIfNeeded()
  }, [tenantLoaded, fetchTenantsIfNeeded])

  async function load(force = false) {
    setError(null)

    // Phase 1: Cached Snapshot aus DB (instant, < 200ms) — zeigt sofort Daten.
    // Nur wenn noch nichts angezeigt wird UND nicht force (bei force direkt Phase 2).
    if (!force && !data) {
      setLoading(true)
      try {
        const snap = await apiClient.get<OposResponse>('/buchhaltung/sap/opos', {
          params: { cachedOnly: 'true' },
          timeout: 10_000,
        })
        if (snap.data?.items?.length || snap.data?.debug?.length) {
          setData(snap.data)
          setLoading(false)
        }
      } catch {
        // Snapshot-Load darf nicht blockieren — Phase 2 laedt ohnehin
      }
    } else if (force) {
      // Bei force: komplett neu laden
      setLoading(true)
    }

    // Phase 2: Live-Daten aus SAP. Laeuft im Hintergrund wenn Snapshot schon sichtbar.
    if (data && !force) setRefreshing(true)
    try {
      const params: Record<string, string> = {}
      if (force) params.force = 'true'
      const res = await apiClient.get<OposResponse>('/buchhaltung/sap/opos', {
        params,
        timeout: 120_000, // 2 min – SAP kann bei vielen offenen Posten lange brauchen
      })
      setData(res.data)
      if (force) setPaymentBlockOverrides(new Map()) // Overrides nach SAP-Reload verwerfen
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ?? (err as { message?: string })?.message ?? 'Fehler beim Laden'
      // Wenn Snapshot da ist, nur als Warnung markieren statt UI zu blockieren
      if (data) {
        setError(`SAP-Aktualisierung fehlgeschlagen: ${msg}. Zeige letzte bekannte Daten.`)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  async function handleTogglePaymentBlock(inv: OposInvoice, block: boolean) {
    const key = `${inv.tenantId}:${inv.DocEntry}:${inv.DocType}`
    // Optimistisches Update
    setPaymentBlockOverrides((prev) => new Map(prev).set(key, block))
    try {
      await apiClient.patch('/buchhaltung/sap/opos/payment-block', {
        tenantId: inv.tenantId,
        docEntry: inv.DocEntry,
        docType: inv.DocType,
        block,
      })
    } catch (err: unknown) {
      // Revert bei Fehler
      setPaymentBlockOverrides((prev) => {
        const next = new Map(prev)
        next.delete(key)
        return next
      })
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ?? (err as { message?: string })?.message ?? 'Fehler beim Setzen der Zahlungssperre'
      setError(msg)
    }
  }

  useEffect(() => { void load() }, [])

  // Gefiltert nach ausgewaehlten Mandanten
  const DEBITOR_TYPES = new Set(['Invoice', 'CreditNote', 'DownPayment', 'IncomingPaymentOnAccount'])
  const KREDITOR_TYPES = new Set(['PurchaseInvoice', 'PurchaseCreditNote', 'PurchaseDownPayment', 'VendorPaymentOnAccount'])

  const debitoren = useMemo(
    () => data?.items.filter((i) => selectedTenants.has(i.tenantId) && DEBITOR_TYPES.has(i.DocType)) ?? [],
    [data, selectedTenants], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const kreditoren = useMemo(
    () => data?.items.filter((i) => selectedTenants.has(i.tenantId) && KREDITOR_TYPES.has(i.DocType)) ?? [],
    [data, selectedTenants], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Mahnsperre-Set aus der API-Response
  const dunningBlockedSet = useMemo(
    () => new Set<string>(data?.dunningBlocked ?? []),
    [data],
  )

  // SAP-Kontensalden: Key = tenantId:CardCode → authoritative SAP-Balance
  const bpBalanceMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const bp of data?.bpBalances ?? []) {
      map.set(`${bp.tenantId}:${bp.CardCode}`, bp.Balance)
    }
    return map
  }, [data])

  // Debug: Gruppen VOR Filter – zeigt warum Partner gezeigt/gefiltert werden
  const debugDebitoren = useMemo(() => showDebug ? buildDebugGroups(debitoren, bpBalanceMap) : [], [showDebug, debitoren, bpBalanceMap])  
  const debugKreditoren = useMemo(() => showDebug ? buildDebugGroups(kreditoren, bpBalanceMap) : [], [showDebug, kreditoren, bpBalanceMap])  

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OPOS – Offene Posten</h1>
          <p className="text-sm text-gray-500 mt-1">Offene Forderungen und Verbindlichkeiten aus SAP Business One</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDebug((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-500 transition-colors"
          >
            {showDebug ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Debug
          </button>
          <button
            onClick={() => void load(true)}
            disabled={loading || refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={cn('w-4 h-4', (loading || refreshing) && 'animate-spin')} />
            {refreshing ? 'Aktualisiere…' : 'Aktualisieren (SAP)'}
          </button>
        </div>
      </div>

      {/* Global error */}
      {error && (
        <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Fehler beim Laden der OPOS-Daten</p>
            <p className="mt-1 text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Stale / Partial indicator – Daten kommen aus DB-Snapshot (SAP war nicht erreichbar) */}
      {data?.stale && (
        <div className="mb-4 flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3 text-sm">
          <Clock className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">
              {refreshing ? 'Aktualisiere Daten aus SAP…' : 'Daten aus zwischengespeichertem Snapshot'}
            </p>
            <p className="mt-0.5 text-xs text-blue-600">
              Letzte SAP-Aktualisierung:{' '}
              {(() => {
                const ts = data.debug?.find((d) => d.loadedAt)?.loadedAt
                return ts ? new Date(ts).toLocaleString('de-DE') : 'unbekannt'
              })()}
              {' · '}
              {refreshing ? 'Live-Daten werden nachgeladen.' : 'Klicke „Aktualisieren (SAP)" für frische Daten.'}
            </p>
          </div>
        </div>
      )}

      {/* Partial-Warnung: einige Endpunkte sind fehlgeschlagen (Timeout) */}
      {data?.partial && !data?.stale && (
        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Unvollständige Daten: einige SAP-Endpunkte waren langsam/nicht erreichbar</p>
            <p className="mt-0.5 text-xs text-amber-600">Details im Debug-Panel (oben rechts).</p>
          </div>
        </div>
      )}

      {/* Tenant errors (partial failures) */}
      {data?.errors && data.errors.length > 0 && (
        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">SAP nicht verfügbar für: {data.errors.map((e) => e.tenantName).join(', ')}</p>
            <details className="mt-1 cursor-pointer">
              <summary className="text-xs text-amber-600">Details anzeigen</summary>
              <ul className="mt-1 space-y-1">
                {data.errors.map((e) => (
                  <li key={e.tenantId} className="text-xs text-amber-600">{e.tenantName}: {e.error}</li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      )}

      {/* Debug panel – hohes Level */}
      {showDebug && data && (
        <div className="mb-4 border-2 border-yellow-300 rounded-lg bg-yellow-50 p-4 text-xs font-mono space-y-4">
          <p className="font-bold text-yellow-800 text-sm font-sans">🔍 DEBUG – Hohes Level</p>

          {/* 1. Belegzaehler pro Mandant */}
          <details open>
            <summary className="cursor-pointer font-semibold text-yellow-700 font-sans text-xs mb-1">1. Belegzähler pro Mandant (aus SAP)</summary>
            <div className="mt-1 space-y-1">
              <DataTable<OposDebugEntry>
                data={data.debug ?? []}
                getRowId={(d) => d.tenantId}
                enableSorting={false}
                columns={[
                  { accessorKey: 'tenantName', header: 'Mandant', cell: ({ row }) => <span className="text-gray-700">{row.original.tenantName}</span> },
                  { id: 'invoice', header: 'Rechng', cell: ({ row }) => <span className="block text-right tabular-nums">{row.original.counts['Invoice'] ?? 0}</span> },
                  { id: 'purchaseInvoice', header: 'ER', cell: ({ row }) => <span className="block text-right tabular-nums">{row.original.counts['PurchaseInvoice'] ?? 0}</span> },
                  { id: 'creditNote', header: 'Gutschr', cell: ({ row }) => <span className="block text-right tabular-nums text-blue-600">{row.original.counts['CreditNote'] ?? 0}</span> },
                  { id: 'purchaseCreditNote', header: 'EGutschr', cell: ({ row }) => <span className="block text-right tabular-nums text-blue-600">{row.original.counts['PurchaseCreditNote'] ?? 0}</span> },
                  { id: 'downPayment', header: 'Anzahl', cell: ({ row }) => <span className="block text-right tabular-nums text-amber-600">{row.original.counts['DownPayment'] ?? 0}</span> },
                  { id: 'purchaseDownPayment', header: 'EAnz.', cell: ({ row }) => <span className="block text-right tabular-nums text-amber-600">{row.original.counts['PurchaseDownPayment'] ?? 0}</span> },
                  { id: 'incomingPayment', header: 'ZahlK', cell: ({ row }) => <span className="block text-right tabular-nums text-teal-600">{row.original.counts['IncomingPaymentOnAccount'] ?? 0}</span> },
                  { id: 'vendorPayment', header: 'Liefk.', cell: ({ row }) => <span className="block text-right tabular-nums text-teal-600">{row.original.counts['VendorPaymentOnAccount'] ?? 0}</span> },
                ]}
              />
              <p className="text-gray-400 text-xs">Filter: DocumentStatus=bost_Open, $top=2000, Prefer:odata.maxpagesize=500 · Zahlungen auf Konto: DocumentStatus=bost_Open (noch nicht gegen Rechnung abgestimmt)</p>
            </div>
          </details>

          {/* 1b. Endpoint-Timings pro Mandant – zeigt welche Endpunkte langsam sind */}
          <details>
            <summary className="cursor-pointer font-semibold text-yellow-700 font-sans text-xs mb-1">
              1b. SAP-Endpoint-Timings (Performance)
            </summary>
            <div className="mt-1 space-y-2">
              {(data.debug ?? []).map((d) => (
                <div key={d.tenantId} className="border border-yellow-200 rounded p-2">
                  <div className="flex items-center gap-2 mb-1 font-sans text-xs">
                    <span className="font-semibold text-gray-700">{d.tenantName}</span>
                    {d.stale && <Badge variant="info" size="sm">SNAPSHOT</Badge>}
                    {d.partial && <Badge variant="warning" size="sm">PARTIAL</Badge>}
                    {d.loadedAt ? <span className="text-gray-400 text-[10px]">geladen: {new Date(d.loadedAt).toLocaleTimeString('de-DE')}</span> : null}
                  </div>
                  <DataTable<OposEndpointTiming>
                    data={d.timings ?? []}
                    getRowId={(_t: OposEndpointTiming, i: number) => String(i)}
                    enableSorting={false}
                    emptyState={<div className="px-3 py-2 text-gray-400 italic text-xs">keine Timings verfügbar</div>}
                    columns={[
                      { accessorKey: 'path', header: 'Endpoint', cell: ({ row }) => <span className="text-gray-700">{row.original.path}</span> },
                      { id: 'ok', header: 'Status', cell: ({ row }) => (
                        <span className={cn('block text-right', row.original.ok ? 'text-green-600' : 'text-red-600')}>{row.original.ok ? '✓' : '✗'}</span>
                      ) },
                      { accessorKey: 'durationMs', header: 'Dauer', cell: ({ row }) => (
                        <span className={cn('block text-right tabular-nums', row.original.durationMs > 10_000 ? 'text-red-600' : row.original.durationMs > 5_000 ? 'text-amber-600' : 'text-gray-700')}>{row.original.durationMs} ms</span>
                      ) },
                      { accessorKey: 'records', header: 'Datensätze', cell: ({ row }) => <span className="block text-right tabular-nums">{row.original.records}</span> },
                      { accessorKey: 'error', header: 'Fehler', cell: ({ row }) => <span className="text-red-500 text-[10px] truncate max-w-[300px] block">{row.original.error ?? ''}</span> },
                    ]}
                  />
                </div>
              ))}
            </div>
          </details>

          {/* 2. SAP BusinessPartners.Balance (raw) */}
          <details open>
            <summary className="cursor-pointer font-semibold text-yellow-700 font-sans text-xs mb-1">
              2. SAP BP-Salden (BusinessPartners.Balance) – {data.bpBalances?.length ?? 0} Einträge
              {' '}(Deb: {data.bpBalances?.filter(b => b.CardType === 'cCustomer').length ?? 0},
              Kred: {data.bpBalances?.filter(b => b.CardType === 'cSupplier').length ?? 0})
            </summary>
            <div className="overflow-x-auto mt-1 max-h-48 overflow-y-auto">
              {(data.bpErrors?.length ?? 0) > 0 && (
                <div className="mb-2">
                  {data.bpErrors!.map((e, i) => (
                    <p key={i} className="text-red-600 font-semibold text-xs">⚠ {e}</p>
                  ))}
                </div>
              )}
              {(data.bpBalances?.length ?? 0) === 0 ? (
                <p className="text-gray-400 text-xs italic">BP-Salden deaktiviert (SAP-Einzelabfragen timeouten)</p>
              ) : (
                <DataTable<BPBalance>
                  data={data.bpBalances ?? []}
                  getRowId={(bp) => `${bp.tenantId}:${bp.CardCode}`}
                  enableSorting={false}
                  rowClassName={(bp) => (bp.Balance < 0 ? 'bg-blue-50' : undefined)}
                  columns={[
                    { accessorKey: 'tenantId', header: 'Mandant', cell: ({ row }) => <span className="text-gray-500">{row.original.tenantId}</span> },
                    { accessorKey: 'CardCode', header: 'CardCode', cell: ({ row }) => <span className="text-gray-700">{a.code(row.original.CardCode)}</span> },
                    { accessorKey: 'Balance', header: 'Balance (SAP)', cell: ({ row }) => (
                      <span className={cn('block text-right font-semibold tabular-nums', row.original.Balance > 0 ? 'text-green-700' : 'text-blue-700')}>
                        {a.fmtCurrency(row.original.Balance)}
                      </span>
                    ) },
                    { accessorKey: 'CardType', header: 'Typ', cell: ({ row }) => <span className="text-gray-500">{row.original.CardType === 'cCustomer' ? 'Deb' : 'Kred'}</span> },
                  ]}
                />
              )}
            </div>
          </details>

          {/* 3. Debitor-Gruppen Analyse (vor Filter) */}
          <details open>
            <summary className="cursor-pointer font-semibold text-yellow-700 font-sans text-xs mb-1">
              3. Debitor-Gruppen VOR Filter – {debugDebitoren.length} total
              {' '}(angezeigt: {debugDebitoren.filter(g => g.shown).length}, gefiltert: {debugDebitoren.filter(g => !g.shown).length})
            </summary>
            <div className="mt-1 max-h-64 overflow-y-auto">
              <DataTable<DebugPartnerGroup>
                data={debugDebitoren}
                getRowId={(g) => `${g.tenantId}:${g.cardCode}`}
                enableSorting={false}
                rowClassName={(g) => (!g.shown ? 'opacity-50 bg-red-50' : undefined)}
                columns={debugPartnerColumns(a)}
              />
            </div>
          </details>

          {/* 4. Kreditor-Gruppen Analyse (vor Filter) */}
          <details>
            <summary className="cursor-pointer font-semibold text-yellow-700 font-sans text-xs mb-1">
              4. Kreditor-Gruppen VOR Filter – {debugKreditoren.length} total
              {' '}(angezeigt: {debugKreditoren.filter(g => g.shown).length}, gefiltert: {debugKreditoren.filter(g => !g.shown).length})
            </summary>
            <div className="mt-1 max-h-64 overflow-y-auto">
              <DataTable<DebugPartnerGroup>
                data={debugKreditoren}
                getRowId={(g) => `${g.tenantId}:${g.cardCode}`}
                enableSorting={false}
                rowClassName={(g) => (!g.shown ? 'opacity-50 bg-red-50' : undefined)}
                columns={debugPartnerColumns(a)}
              />
            </div>
          </details>

          <p className="text-gray-400 font-sans">Filter-Logik: computedBalance &gt; 0,01 ODER sapBalance &gt; 0,01 → Partner anzeigen. SAP-Saldo negativ = Guthaben (nicht anzeigen). Grüne Werte = über Schwelle.</p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <OposColumn
          title="Debitoren"
          subtitle="Offene Forderungen gegenüber Kunden"
          icon={<TrendingUp className="w-6 h-6 text-white" />}
          invoices={debitoren}
          loading={loading}
          variant="green"
          dunningBlockedSet={dunningBlockedSet}
          paymentBlockOverrides={paymentBlockOverrides}
          onTogglePaymentBlock={handleTogglePaymentBlock}
          bpBalanceMap={bpBalanceMap}
        />
        <OposColumn
          title="Kreditoren"
          subtitle="Offene Verbindlichkeiten gegenüber Lieferanten"
          icon={<TrendingDown className="w-6 h-6 text-white" />}
          invoices={kreditoren}
          loading={loading}
          variant="red"
          dunningBlockedSet={dunningBlockedSet}
          paymentBlockOverrides={paymentBlockOverrides}
          onTogglePaymentBlock={handleTogglePaymentBlock}
          bpBalanceMap={bpBalanceMap}
          isKreditor
        />
      </div>
    </div>
  )
}
